import React, { useEffect, useMemo, useState } from 'react';
import { apiV1 } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, X, Copy, Languages } from 'lucide-react';
import { SUPPORTED_LOCALES, localeName } from '@/lib/locales';

/**
 * Translation editor for a landing page — Phase 2 of multi-language pages.
 *
 * One page structure, per-locale TEXT only. The list of translatable fields
 * comes from the backend (POST /pages/i18n/fields), i.e. the same registry the
 * resolver enforces, so the editor can never offer a field the server won't
 * translate. The base (default-locale) text is shown as a ghost placeholder,
 * and "Copy from base" seeds a locale to start from.
 *
 * Everything is edited on `page.translations[locale][bucket][path]`, where
 * bucket is a section id, "_form", or "_meta". The parent's normal Save writes
 * it — this component only mutates the page object via onChange.
 */

const FORM_BUCKET = '_form';
const META_BUCKET = '_meta';

const SECTION_LABEL = {
  hero: 'Hero', text: 'Text', cta_button: 'Button', contact_form: 'Contact form',
  features: 'Features', testimonial: 'Testimonials', image_gallery: 'Image gallery',
  agent_card: 'Agent card', property_details: 'Property details', map: 'Map',
};

function humanizePath(path) {
  return path
    .split('.')
    .map((p) => (/^\d+$/.test(p) ? `#${Number(p) + 1}` : p.replace(/_/g, ' ')))
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' · ');
}

const isLong = (path, base) =>
  (base && base.length > 60) || /(body|description|message|bio|success)/i.test(path);

export default function TranslationEditor({ page, onChange }) {
  const defaultLocale = page?.default_locale || 'en';
  const locales = useMemo(() => page?.locales || [], [page]);
  const translations = page?.translations || {};

  const [fields, setFields] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(locales[0] || null);

  // Fetch the translatable-field list from the current (possibly unsaved)
  // content, so it always matches what the author sees in the builder.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiV1.post('/pages/i18n/fields', {
      sections: page?.sections || [],
      form: page?.form || null,
      meta_title: page?.meta_title,
      meta_description: page?.meta_description,
    })
      .then((r) => { if (!cancelled) setFields(r.data?.data || null); })
      .catch(() => { if (!cancelled) setFields(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page?.sections, page?.form, page?.meta_title, page?.meta_description]);

  useEffect(() => {
    if (active && !locales.includes(active)) setActive(locales[0] || null);
    if (!active && locales.length) setActive(locales[0]);
  }, [locales, active]);

  const addable = SUPPORTED_LOCALES.filter((l) => l !== defaultLocale && !locales.includes(l));

  // ---- mutations on page.translations -------------------------------------

  const addLocale = (loc) => {
    onChange({ ...page, locales: [...locales, loc] });
    setActive(loc);
  };

  const removeLocale = (loc) => {
    const nextTr = { ...translations };
    delete nextTr[loc];
    onChange({ ...page, locales: locales.filter((l) => l !== loc), translations: nextTr });
  };

  const getVal = (loc, bucket, path) => translations?.[loc]?.[bucket]?.[path] ?? '';

  const setVal = (loc, bucket, path, value) => {
    const next = { ...translations };
    next[loc] = { ...(next[loc] || {}) };
    next[loc][bucket] = { ...(next[loc][bucket] || {}) };
    if (value === '') delete next[loc][bucket][path];
    else next[loc][bucket][path] = value;
    onChange({ ...page, translations: next });
  };

  // Buckets the editor renders, as [{key, label, fields:[{path,base}]}]
  const buckets = useMemo(() => {
    if (!fields) return [];
    const out = (fields.sections || []).map((s) => ({
      key: s.id,
      label: SECTION_LABEL[s.type] || s.type,
      fields: s.fields || [],
    }));
    if (fields.form?.fields?.length) out.push({ key: FORM_BUCKET, label: 'Form', fields: fields.form.fields });
    if (fields.meta?.fields?.length) out.push({ key: META_BUCKET, label: 'SEO / meta', fields: fields.meta.fields });
    return out;
  }, [fields]);

  const allFieldRefs = useMemo(
    () => buckets.flatMap((b) => b.fields.map((f) => ({ bucket: b.key, path: f.path, base: f.base }))),
    [buckets]
  );

  const completeness = (loc) => {
    const total = allFieldRefs.length;
    const done = allFieldRefs.filter((r) => getVal(loc, r.bucket, r.path)).length;
    return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
  };

  const copyFromBase = (loc, bucketKey) => {
    const next = { ...translations };
    next[loc] = { ...(next[loc] || {}) };
    const refs = bucketKey
      ? allFieldRefs.filter((r) => r.bucket === bucketKey)
      : allFieldRefs;
    refs.forEach((r) => {
      if (!getVal(loc, r.bucket, r.path) && r.base) {
        next[loc][r.bucket] = { ...(next[loc][r.bucket] || {}), [r.path]: r.base };
      }
    });
    onChange({ ...page, translations: next });
  };

  // ---- render -------------------------------------------------------------

  return (
    <div className="space-y-4" data-testid="translation-editor">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Languages className="h-4 w-4" />
        <span><strong className="text-foreground">{localeName(defaultLocale)}</strong> is the base language. Add a language, then translate the text below — anything left blank falls back to {localeName(defaultLocale)}.</span>
      </div>

      {/* Enabled locales + add */}
      <div className="flex flex-wrap items-center gap-2">
        {locales.map((loc) => {
          const c = completeness(loc);
          return (
            <button
              key={loc}
              type="button"
              onClick={() => setActive(loc)}
              data-testid={`locale-tab-${loc}`}
              className={`group flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm ${
                active === loc ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
              }`}
            >
              <span>{localeName(loc)}</span>
              <span className="text-xs tabular-nums text-muted-foreground">{c.done}/{c.total}</span>
              <X
                className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                onClick={(e) => { e.stopPropagation(); removeLocale(loc); }}
                data-testid={`locale-remove-${loc}`}
              />
            </button>
          );
        })}

        {addable.length > 0 && (
          <select
            className="h-8 rounded-md border border-dashed border-input bg-background px-2 text-sm text-muted-foreground"
            value=""
            onChange={(e) => e.target.value && addLocale(e.target.value)}
            data-testid="add-locale"
          >
            <option value="">+ Add language</option>
            {addable.map((l) => <option key={l} value={l}>{localeName(l)}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading fields…
        </div>
      ) : locales.length === 0 ? (
        <p className="rounded-md border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
          Add a language to start translating this page.
        </p>
      ) : !active ? null : allFieldRefs.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          This page has no translatable text yet — add some content first.
        </p>
      ) : (
        <div className="space-y-5" data-testid={`locale-panel-${active}`}>
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="font-medium">{localeName(active)}</span>{' '}
              <span className="text-muted-foreground">
                {completeness(active).done}/{completeness(active).total} translated
              </span>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => copyFromBase(active, null)}
              data-testid="copy-all">
              <Copy className="mr-1 h-3.5 w-3.5" /> Copy all from {localeName(defaultLocale)}
            </Button>
          </div>

          {buckets.map((b) => (
            <div key={b.key} className="rounded-md border border-border p-3">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-semibold">{b.label}</h4>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs"
                  onClick={() => copyFromBase(active, b.key)} data-testid={`copy-${b.key}`}>
                  <Copy className="mr-1 h-3 w-3" /> Copy section
                </Button>
              </div>
              <div className="space-y-3">
                {b.fields.map((f) => {
                  const val = getVal(active, b.key, f.path);
                  const Comp = isLong(f.path, f.base) ? Textarea : Input;
                  return (
                    <div key={f.path}>
                      <Label className="text-xs text-muted-foreground">{humanizePath(f.path)}</Label>
                      <Comp
                        value={val}
                        onChange={(e) => setVal(active, b.key, f.path, e.target.value)}
                        placeholder={f.base || ''}
                        className="mt-1"
                        data-testid={`tr-${b.key}-${f.path}`}
                        {...(Comp === Textarea ? { rows: 2 } : {})}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
